// Gedeeld rijmodel voor de begrotingsmatrix, de vergelijkings-PDF en de kolommen
// "Resultaat <jaar>". Zelfde opbouw als de matrix Voorgaande jaren.
export const BD_FIELD = { revenue: "revenue_breakdown", opex: "opex_breakdown" };

const num = (v) => Number(v) || 0;

export function subpostNames(category, subposts, rows) {
  const names = [];
  for (const s of subposts || []) {
    if (s.category === category && s.name && !names.includes(s.name)) names.push(s.name);
  }
  for (const r of rows || []) {
    for (const it of (r?.[BD_FIELD[category]] || [])) {
      const n = String(it?.name || "").trim();
      if (n && !names.includes(n)) names.push(n);
    }
  }
  return names;
}

export const bdAmount = (row, category, name) => {
  const hit = (row?.[BD_FIELD[category]] || []).find((it) => String(it?.name || "").trim() === name);
  return hit ? num(hit.amount) : null;
};

const sumBd = (r, cat) => (r?.[BD_FIELD[cat]] || []).reduce((a, b) => a + num(b.amount), 0);

export const calc = {
  inkomsten: (r) => num(r.revenue) + num(r.other_income),
  kosten: (r) => num(r.operating_costs),
  afschrijving: (r) => num(r.depreciation),
  overigeInkomsten: (r) => calc.inkomsten(r) - sumBd(r, "revenue"),
  overigeKosten: (r) => calc.kosten(r) - sumBd(r, "opex"),
  resultaat: (r) => calc.inkomsten(r) - calc.kosten(r) - calc.afschrijving(r),
  netto: (r) => calc.resultaat(r) - (num(r.interest_paid) - num(r.interest_income)),
};

// kind: agg (bewerkbaar totaal) · sub (subpost, bewerkbaar) · sys (residu) · computed · field (bewerkbaar veld)
export function buildBudgetRows(t, subposts, rows) {
  const rev = subpostNames("revenue", subposts, rows);
  const opx = subpostNames("opex", subposts, rows);
  return [
    { key: "inkomsten", label: t("Inkomsten"), kind: "agg", field: "revenue", cat: "revenue", value: calc.inkomsten },
    ...rev.map((n) => ({ key: `rev:${n}`, label: n, kind: "sub", cat: "revenue", name: n, value: (r) => bdAmount(r, "revenue", n) })),
    { key: "overige-inkomsten", label: t("Overige inkomsten"), kind: "sys", addCat: "revenue", value: calc.overigeInkomsten },
    { key: "kosten", label: t("Kosten"), kind: "agg", field: "operating_costs", cat: "opex", value: calc.kosten },
    ...opx.map((n) => ({ key: `opex:${n}`, label: n, kind: "sub", cat: "opex", name: n, value: (r) => bdAmount(r, "opex", n) })),
    { key: "overige-kosten", label: t("Overige kosten"), kind: "sys", addCat: "opex", value: calc.overigeKosten },
    { key: "afschrijving", label: t("Afschrijving"), kind: "agg", field: "depreciation", value: calc.afschrijving },
    { key: "resultaat", label: t("Exploitatieresultaat"), kind: "computed", strong: true, value: calc.resultaat },
    { key: "rentelasten", label: t("Rentelasten"), kind: "field", field: "interest_paid", value: (r) => num(r.interest_paid) },
    { key: "rentebaten", label: t("Rentebaten"), kind: "field", field: "interest_income", value: (r) => num(r.interest_income) },
    { key: "netto", label: t("Netto exploitatieresultaat"), kind: "computed", strong: true, value: calc.netto },
    { key: "kas", label: t("Kas + bank 31/12"), kind: "field", field: "cash_bank_balance", value: (r) => num(r.cash_bank_balance) },
  ];
}

// Kostenachtige rijen: meer realisatie dan begroot = ongunstig.
export const isCostRow = (row) => row.cat === "opex" || row.addCat === "opex" || row.key === "afschrijving" || row.key === "rentelasten";

// Volledige PUT-body voor /budgets/{id} (BudgetIn) met een patch erop.
export function budgetPayload(b, patch = {}) {
  const m = { ...b, ...patch };
  return {
    year: parseInt(m.year, 10),
    revenue: num(m.revenue),
    operating_costs: num(m.operating_costs),
    depreciation: num(m.depreciation),
    interest_paid: num(m.interest_paid),
    interest_income: num(m.interest_income),
    other_income: num(m.other_income),
    cash_bank_balance: num(m.cash_bank_balance),
    revenue_breakdown: m.revenue_breakdown || [],
    opex_breakdown: m.opex_breakdown || [],
    other_income_breakdown: m.other_income_breakdown || [],
    notes: m.notes || "",
  };
}

// Patch voor een celwijziging. Inkomsten-totaal bewerkt `revenue` zodat het totaal exact v wordt.
export function fieldPatch(b, field, v) {
  if (field === "revenue") return { revenue: num(v) - num(b.other_income) };
  return { [field]: num(v) };
}

export function subpostPatch(b, cat, name, v) {
  const key = BD_FIELD[cat];
  const arr = (b[key] || []).map((x) => ({ ...x }));
  const hit = arr.find((x) => String(x?.name || "").trim() === name);
  if (hit) hit.amount = num(v); else arr.push({ name, amount: num(v) });
  return { [key]: arr };
}
